import { useMemo, useState, useCallback } from 'react'
import { toast } from 'react-toastify'
import { getSPDataWithParams } from 'src/supportPortal/services/services'

/**
 * Custom hook for patient table data using the new patient search API
 * API: GET /api/secure/cc/support/patient/search
 *
 * Supports all filters from the API:
 * - clinicBrandId (optional - for scoping to a specific brand)
 * - clinicId (optional)
 * - search (free text - name, mobile, patientNumber)
 * - includeImportId, includeUhId (search options)
 * - practitionerId / mentorId
 * - eventType (list)
 * - monitored (boolean)
 * - monitoringScope, monitoringPolicy
 * - monitoringEventModes, monitoringEventTypes, monitoringActions (lists)
 * - page, pageSize
 */
export const usePatientTableData = (referenceData) => {
  const pageSize = 20

  // State
  const [rawPatients, setRawPatients] = useState([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 0,
    totalElements: 0,
    size: pageSize,
  })
  const [filters, setFilters] = useState({
    // Basic filters
    mentorId: '',
    clinicBrandId: '',
    clinicId: '',
    eventTypes: [],
    search: '',
    // Search options
    includeImportId: false,
    includeUhId: false,
    // Monitoring filters
    monitored: null, // null = any, true = monitored, false = not monitored
    monitoringScope: '',
    monitoringPolicy: '',
    monitoringEventModes: [],
    monitoringEventTypes: [],
    monitoringActions: [],
  })

  // Fetch data with current filters
  const fetchData = useCallback(
    async (filterOverrides = {}) => {
      setLoading(true)
      try {
        const currentFilters = { ...filters, ...filterOverrides }
        const params = {
          page: currentFilters.page || pagination.page,
          pageSize: pagination.size,
        }

        // Add clinicBrandId (optional - for scoping to a specific brand)
        if (currentFilters.clinicBrandId) {
          params.clinicBrandId = currentFilters.clinicBrandId
        }

        // Add clinicId if provided
        if (currentFilters.clinicId) {
          params.clinicId = currentFilters.clinicId
        }

        // Add search query
        if (currentFilters.search) {
          params.search = currentFilters.search
        }

        // Search options
        if (currentFilters.includeImportId) {
          params.includeImportId = 'true'
        }
        if (currentFilters.includeUhId) {
          params.includeUhId = 'true'
        }

        // Add practitioner/mentor filter
        if (currentFilters.mentorId) {
          params.practitionerId = currentFilters.mentorId
        }

        // Add eventType filter (can be array)
        if (currentFilters.eventTypes && currentFilters.eventTypes.length > 0) {
          params.eventType = currentFilters.eventTypes.join(',')
        }

        // Include engagement details for showing engagement status
        params.includeEngagementDetails = 'true'
        
        // Include event types for showing patient event type icons
        params.includeEventTypes = 'true'

        // Monitoring filters
        if (currentFilters.monitored !== null && currentFilters.monitored !== '') {
          params.monitored = String(currentFilters.monitored)
        }
        if (currentFilters.monitoringScope) {
          params.monitoringScope = currentFilters.monitoringScope
        }
        if (currentFilters.monitoringPolicy) {
          params.monitoringPolicy = currentFilters.monitoringPolicy
        }
        if (currentFilters.monitoringEventModes?.length > 0) {
          params.monitoringEventModes = currentFilters.monitoringEventModes.join(',')
        }
        if (currentFilters.monitoringEventTypes?.length > 0) {
          params.monitoringEventTypes = currentFilters.monitoringEventTypes.join(',')
        }
        if (currentFilters.monitoringActions?.length > 0) {
          params.monitoringActions = currentFilters.monitoringActions.join(',')
        }

        const resp = await getSPDataWithParams(
          '/api/secure/cc/support/patient/search',
          params,
          undefined,
          undefined,
          true, // useDataMap
        )

        if (resp.status === 200) {
          // The API returns a simple array of PatientInfo objects
          const responseData = Array.isArray(resp.data)
            ? resp.data
            : resp.data?.data || resp.data?.content || []

          setRawPatients(responseData)

          // Calculate pagination based on returned data
          const totalElements = responseData.length
          const hasMore = responseData.length >= pageSize
          setPagination((prev) => ({
            ...prev,
            page: params.page,
            totalElements: totalElements,
            totalPages: hasMore ? params.page + 1 : params.page,
          }))
        } else {
          throw new Error(resp?.data?.message || 'Failed to load patients list')
        }
      } catch (error) {
        console.error('Error fetching patients:', error)
        toast.error('Failed to load patients list')
        setRawPatients([])
      } finally {
        setLoading(false)
      }
    },
    [filters, pagination.page, pagination.size],
  )

  // Update a specific filter
  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }, [])

  // Batch update multiple filters
  const updateFilters = useCallback((updates) => {
    setFilters((prev) => ({ ...prev, ...updates }))
  }, [])

  // Handle search with debouncing logic
  const handleSearch = useCallback(
    (key, value, minLength = 3) => {
      updateFilter(key, value)
      if (value === '' || value.length >= minLength) {
        fetchData({ [key]: value, page: 1 })
      }
    },
    [updateFilter, fetchData],
  )

  // Handle filter change (dropdown, etc.)
  const handleFilterChange = useCallback(
    (key, value) => {
      updateFilter(key, value ?? '')
      fetchData({ [key]: value, page: 1 })
    },
    [updateFilter, fetchData],
  )

  // Handle multiple filter changes at once
  const handleMultipleFilterChange = useCallback(
    (updates) => {
      updateFilters(updates)
      fetchData({ ...updates, page: 1 })
    },
    [updateFilters, fetchData],
  )

  // Handle page change
  const handlePageChange = useCallback(
    (page) => {
      setPagination((prev) => ({ ...prev, page }))
      fetchData({ page })
    },
    [fetchData],
  )

  // Clear all filters except clinicBrandId
  const clearFilters = useCallback(() => {
    const clearedFilters = {
      mentorId: '',
      clinicId: '',
      eventTypes: [],
      search: '',
      includeImportId: false,
      includeUhId: false,
      monitored: null,
      monitoringScope: '',
      monitoringPolicy: '',
      monitoringEventModes: [],
      monitoringEventTypes: [],
      monitoringActions: [],
    }
    setFilters((prev) => ({ ...prev, ...clearedFilters }))
    fetchData({ ...clearedFilters, page: 1 })
  }, [fetchData])

  // Enrich patients with reference data
  const patients = useMemo(() => {
    const { clinicBrands = [], clinicLocations = [] } = referenceData
    return rawPatients.map((patient) => ({
      ...patient,
      brandData: clinicBrands.find((brand) => brand.id === patient.clinicBrandId),
      clinicData: clinicLocations.find((location) => location.id === patient.clinicId),
    }))
  }, [rawPatients, referenceData])

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (filters.mentorId) count++
    if (filters.clinicId) count++
    if (filters.eventTypes?.length > 0) count++
    if (filters.search) count++
    if (filters.includeImportId) count++
    if (filters.includeUhId) count++
    if (filters.monitored !== null && filters.monitored !== '') count++
    if (filters.monitoringScope) count++
    if (filters.monitoringPolicy) count++
    if (filters.monitoringEventModes?.length > 0) count++
    if (filters.monitoringEventTypes?.length > 0) count++
    if (filters.monitoringActions?.length > 0) count++
    return count
  }, [filters])

  return {
    patients,
    loading,
    pagination,
    filters,
    activeFilterCount,
    fetchData,
    handleSearch,
    handleFilterChange,
    handleMultipleFilterChange,
    handlePageChange,
    clearFilters,
  }
}
